import { createContext, useContext } from "react";
import type { Strings } from "./strings/en";

/**
 * Firebase-free i18n leaf. Safe to import from the static prerender bundle and
 * from any component. The provider (./LocaleProvider) owns state + persistence.
 *
 * Locales: English, Uzbek (Latin script, the official orthography) and Russian.
 * Uzbek Cyrillic is intentionally not offered.
 */
export type Locale = "en" | "uz" | "ru";
export const LOCALES: Locale[] = ["en", "uz", "ru"];

/** Native-script names for the language picker. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  uz: "Oʻzbekcha",
  ru: "Русский",
};

/** BCP-47 tags for Intl date/number formatting. */
export const LOCALE_TAG: Record<Locale, string> = {
  en: "en-US",
  uz: "uz-Latn-UZ",
  ru: "ru-RU",
};

export const LOCALE_STORAGE_KEY = "om.locale";

export function isLocale(v: unknown): v is Locale {
  return v === "en" || v === "uz" || v === "ru";
}

export interface LocaleValue {
  locale: Locale;
  strings: Strings;
  setLocale: (l: Locale) => void;
}

export const LocaleContext = createContext<LocaleValue | null>(null);

export function useLocale(): LocaleValue {
  const v = useContext(LocaleContext);
  if (!v) throw new Error("useLocale must be used inside <LocaleProvider>");
  return v;
}

/** The whole typed dictionary for the active locale: `const s = useT(); s.landing.hero.h1`. */
export function useT(): Strings {
  return useLocale().strings;
}
